import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreens } from 'components/Navigation/types';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ALERT, doToast, NAVIGATE_MODAL } from 'store/actions';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Icon, ICONS } from 'components/Icon';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import {
  filterArrayEmpty,
  formatDate,
  isoDateStringToDate,
  openUrl,
  urlWithQuery,
} from 'lib/riskbase';
import { ScreenSize, UI_DATE_FORMAT_LONG } from 'lib/constants';
import { CommunicationAccessedFrom, CommunicationType } from 'types/Engage';
import { CommunicationResponseStatus } from 'types/DTOs/BlockManagerDTOs';
import type { EngageCommunicationListItem } from 'types/Engage';
import type { ISODateString } from 'types/types';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import { Button } from 'components/Button';
import { IconButton } from 'components/IconButton';
import { PageScrollView } from 'components/Page';
import * as Clipboard from 'expo-clipboard';

type TimelineItem = {
  key: string;
  label: string;
  date: ISODateString | null;
  variant:
    | 'sent'
    | 'opened'
    | 'due'
    | 'overdue'
    | 'expired'
    | 'responded'
    | 'failed';
};

const getTimelineColors = (variant: TimelineItem['variant']) => {
  switch (variant) {
    case 'responded':
      return { background: theme.colors.green, text: theme.colors.white };
    case 'expired':
      return { background: theme.colors.orange, text: theme.colors.white };
    case 'overdue':
      return { background: theme.colors.orange, text: theme.colors.white };
    case 'due':
      return { background: theme.colors.background, text: theme.colors.black };
    case 'opened':
      return { background: theme.colors.background, text: theme.colors.black };
    case 'failed':
      return { background: theme.colors.red, text: theme.colors.white };
    case 'sent':
    default:
      return { background: theme.colors.background, text: theme.colors.black };
  }
};

const getTimelineLabel = (item: TimelineItem) => {
  const dateLabel = item.date
    ? formatDate(isoDateStringToDate(item.date), UI_DATE_FORMAT_LONG)
    : null;
  return dateLabel ? `${item.label} ${dateLabel}` : item.label;
};

const buildCommunicationTimeline = (
  communication: EngageCommunicationListItem,
): TimelineItem[] => {
  const flags = communication.statusFlags;
  if (!flags) {
    return [];
  }

  const timeline: TimelineItem[] = [
    {
      key: 'sent',
      label: 'Sent',
      date: flags.sentDate ?? communication.createdDate,
      variant: 'sent',
    },
  ];

  if (flags.opened) {
    timeline.push({
      key: 'opened',
      label: 'Opened',
      date: flags.openedDate,
      variant: 'opened',
    });
  }

  const failure =
    (flags.bounced && {
      key: 'bounced',
      label: 'Failed: Bounced',
      date: flags.bouncedDate,
      variant: 'failed' as const,
    }) ||
    (flags.rejected && {
      key: 'rejected',
      label: 'Failed: Rejected',
      date: flags.rejectedDate,
      variant: 'failed' as const,
    }) ||
    (flags.delayed && {
      key: 'delayed',
      label: 'Failed: Delayed',
      date: flags.delayedDate,
      variant: 'failed' as const,
    });

  const terminal =
    failure ||
    (flags.responded && {
      key: 'responded',
      label: 'Responded',
      date: flags.respondedDate,
      variant: 'responded' as const,
    }) ||
    (flags.expired && {
      key: 'expired',
      label: 'Expired',
      date: flags.expiredDate,
      variant: 'expired' as const,
    }) ||
    (flags.overDue && {
      key: 'overdue',
      label: 'Response Overdue',
      date: flags.overDueDate,
      variant: 'overdue' as const,
    }) ||
    (flags.due && {
      key: 'due',
      label: 'Response Due',
      date: flags.dueDate,
      variant: 'due' as const,
    });

  if (terminal) {
    timeline.push(terminal);
  }

  return timeline;
};

export const EngageFlatModalCommunicationsPage: React.FC<{
  pageKey: string;
  HeaderItems: React.ReactNode;
  contactId: string | null;
}> = ({ pageKey: key, HeaderItems, contactId }) => {
  const dispatch = useDispatch();
  const {
    flat: { id: flatId },
    property: { id: propertyId },
    contacts,
    communications,
  } = useSelector((state) => selectEngageFlat(state, key))!;
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const isWeb = useScreenSize() === ScreenSize.large;
  const scrollPaddingBottom = isWeb
    ? theme.paddingPageVertical * 10 + safeAreaBottom
    : theme.paddingPageVertical * 10 + safeAreaBottom;

  const filteredCommunications = contactId
    ? communications.filter(
        (communication) => communication.contactId === contactId,
      )
    : communications;

  const handleSendCommunication = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageSendCommunication,
        params: {
          key,
          flatIds: [flatId],
          propertyId,
          contactId,
          mode: 'singleFlat',
        },
      }),
    );
  }, [contactId, dispatch, flatId, key, propertyId]);

  const handlePreviewCommunication = (
    communication: EngageCommunicationListItem,
  ) => {
    if (!communication.previewContentURL) {
      dispatch(
        ALERT({
          text: 'Preview Not Available',
        }),
      );
      return;
    }

    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.PDFVIEWER,
        params: {
          title: communication.name,
          sourceUrl: communication.previewContentURL,
        },
      }),
    );
  };

  const handleLink = async (link: string, copy: boolean) => {
    const finalLink = urlWithQuery(link, {
      source: CommunicationAccessedFrom.app,
    });
    if (copy) {
      const copied = await Clipboard.setStringAsync(finalLink);
      if (copied) {
        dispatch(doToast('Link Copied'));
        return;
      }
    }
    openUrl(finalLink);
  };

  const large = useScreenSize() === ScreenSize.large;

  return (
    <View style={{ flex: 1 }}>
      <PageScrollView
        isInModal={true}
        ignoreSafeAreaBottom
        contentPaddingBottom={scrollPaddingBottom}
        header={HeaderItems}
      >
        <Card>
          {filteredCommunications.length === 0 ? (
            <CardRow value="No Messages Sent" />
          ) : (
            filteredCommunications.map((communication) => {
              const address = communication.address
                .split('\\n')
                .filter(filterArrayEmpty)
                .join(', ');
              const isLinkExpired =
                communication.linkStatus ===
                CommunicationResponseStatus.expired;
              const communicationContact = communication.contactId
                ? (contacts.find((c) => c.id === communication.contactId) ?? {
                    firstName: 'Archived Contact',
                    lastName: '',
                  })
                : null;
              const timeline = buildCommunicationTimeline(communication);
              return (
                <CardRow
                  key={communication.id}
                  labelAlignTop
                  showMenuOnPress
                  showMenuOnContext
                  menuOptions={[
                    {
                      text: 'Preview Message',
                      onPress: () => handlePreviewCommunication(communication),
                    },
                    ...(communication.link && !isLinkExpired
                      ? [
                          communication.link,
                          {
                            text: 'Open Link',
                            onPress: () =>
                              handleLink(communication.link!, false),
                            icon: ICONS.ArrowUpRightFromSquareLight,
                          },
                          {
                            text: 'Copy Link',
                            onPress: () =>
                              handleLink(communication.link!, true),
                            icon: ICONS.CopyLight,
                          },
                        ]
                      : ['Link is no longer available']),
                  ]}
                >
                  <View
                    style={{
                      flexDirection: 'row',

                      alignItems: 'center',
                      columnGap: theme.paddingHorizontal,
                      flexWrap: 'wrap',
                    }}
                  >
                    <View
                      style={{
                        alignItems: 'center',
                        width: 72,
                      }}
                    >
                      <View
                        style={{
                          borderRadius: 42,
                          width: 50,
                          height: 50,
                          backgroundColor: theme.colors.black10,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {communication.type === CommunicationType.email ? (
                          <Icon source={ICONS.AtLight} size={25} />
                        ) : (
                          <Icon source={ICONS.FileLight} size={25} />
                        )}
                      </View>
                    </View>
                    <View style={{ flex: 1 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginBottom: 2,
                          columnGap: theme.paddingHorizontalHalf,
                        }}
                      >
                        <Txt key="name" weight="medium" style={{ flex: 1 }}>
                          {communication.name}
                        </Txt>
                      </View>
                      <Txt size="xsmall">
                        {communicationContact
                          ? `${communicationContact.firstName} ${communicationContact.lastName}`.trim() +
                            ` <${address}>`
                          : address}
                      </Txt>

                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: theme.paddingHorizontalHalf,
                          flexWrap: 'wrap',
                          marginTop: theme.paddingHorizontalHalf,
                        }}
                      >
                        {timeline.map((item, index) => {
                          const { background, text } = getTimelineColors(
                            item.variant,
                          );
                          return (
                            <React.Fragment
                              key={`${communication.id}-${item.key}`}
                            >
                              <Pill
                                backgroundColor={background}
                                textColor={text}
                                label={getTimelineLabel(item)}
                                textProps={{
                                  size: 'xxxsmall',
                                  weight: 'bold',
                                }}
                              />
                              {index < timeline.length - 1 && large && (
                                <Icon
                                  source={ICONS.ArrowRightSolid}
                                  size={16}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </View>
                    </View>
                  </View>
                </CardRow>
              );
            })
          )}
        </Card>
      </PageScrollView>
      {isWeb ? (
        <View pointerEvents="box-none" style={styles.webButtonWrapper}>
          <IconButton
            icon={ICONS.PaperPlaneLight}
            onPress={handleSendCommunication}
            size={60}
            iconSize={30}
            color={theme.colors.white}
            style={styles.webButton}
            hoveredStyle={{ backgroundColor: theme.colors.blue90 }}
            pressedStyle={{ backgroundColor: theme.colors.blue80 }}
          />
        </View>
      ) : (
        <View style={styles.mobileBarWrapper}>
          <View
            style={{
              backgroundColor: 'transparent',
              paddingBottom: theme.paddingVerticalHalf,
              borderRadius: 8,
            }}
          >
            <Button
              iconRightSize={20}
              iconRight={ICONS.PaperPlaneLight}
              text="Send Message"
              textSize="large"
              onPress={handleSendCommunication}
              style={styles.mobileBarButton}
            />
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  webButtonWrapper: {
    position: 'absolute',
    right: theme.paddingPageVertical * 2,
    bottom: theme.paddingPageVertical * 2,
    zIndex: 2,
  },
  webButton: {
    backgroundColor: theme.colors.blue,
    shadowColor: theme.colors.black,
    shadowOpacity: 0.35,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 4,
  },
  mobileBarWrapper: {
    position: 'absolute',
    width: '70%',
    left: '15%',
    bottom: 0,
    zIndex: 2,
    shadowRadius: 4,
    shadowOpacity: 0.4,
    shadowOffset: { width: 0, height: 3 },
    marginBottom: theme.paddingPageVertical,
  },
  mobileBarButton: {
    justifyContent: 'center',
    height: 50,
    borderRadius: 999,
  },
});
